import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateQuantity,
  removeItem,
  updatePrice,
  updatePrintName,
  updateCartItem,
} from "../../../ReduxToolkit/Reducers/CartReducer";
import POSTouchSpin from "./Touchspin";
import { getFormattedPrice } from "../../../services/helper";
import ItemAddonView from "../ItemAddonView";
import Swal from "sweetalert2";
import POSAddonModal from "../POSAddonModal";

const CartItems = () => {
  const cartItems = useSelector((state) => state.cart.cartItems);
  const dispatch = useDispatch();

  const [editModal, setEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editAddonQuantities, setEditAddonQuantities] = useState({});

  const handleEditAddons = (item, index) => {
    setEditingItem(item);
    setEditingIndex(index);
    setEditAddonQuantities(item._addonQuantities || {});
    setEditModal(true);
  };

  const handleUpdateCartItemAddons = (selectedAddons, subtotal) => {
    dispatch(updateCartItem({
      index: editingIndex,
      updates: {
        addons: selectedAddons,
        price: subtotal,
        _addonQuantities: editAddonQuantities,
      },
    }));
    setEditModal(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  // console.log("Cart Items:", cartItems); // Debug cart items

  const handleIncreaseQuantity = (id) => {
    const item = cartItems.find((item) => item.id === id);
    if (item) {
      dispatch(updateQuantity({ id, quantity: item.quantity + 1 }));
    }
  };

  const handleDecreaseQuantity = (id) => {
    const item = cartItems.find((item) => item.id === id);
    if (item && item.quantity > 1) {
      dispatch(updateQuantity({ id, quantity: item.quantity - 1 }));
    } else if (item && item.quantity === 1) {
      dispatch(removeItem(id));
    }
  };

  const handleEditPrice = async (item, index) => {
    const { value: newPrice } = await Swal.fire({
      title: `Update Price for "${item.name}"`,
      input: "number",
      inputLabel: "New Price",
      inputValue: item.price,
      inputAttributes: {
        min: "0",
        step: "0.01",
      },
      showCancelButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value || parseFloat(value) <= 0) {
          return "Please enter a valid price";
        }
      },
    });

    if (newPrice) {
      dispatch(updatePrice({ index, price: parseFloat(newPrice) }));
    }
  };

  const handleEditPrintName = async (item, index) => {
    const { value: newPrintName } = await Swal.fire({
      title: `Update Print Name for "${item.name}"`,
      input: "text",
      inputLabel: "Print Name (for receipt only)",
      inputValue: item.print_name || item.name,
      showCancelButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "Please enter a valid name";
        }
      },
    });

    if (newPrintName) {
      dispatch(updatePrintName({ index, print_name: newPrintName.trim() }));
    }
  };

  const handleRemoveItem = (id) => {
    dispatch(removeItem(id));
  };

  return (
    <div className="cart-items">
      {cartItems.length === 0 ? (
        <div className="empty-cart waving-cart">
          <span>Your cart is empty</span>
        </div>
      ) : (
        cartItems.map((item, index) => {
          // ✅ Parse `addons` from JSON string (fix)
          let addons = {};
          try {
            addons =
              typeof item.addons === "string"
                ? JSON.parse(item.addons)
                : item.addons;
          } catch (error) {
            console.error("Error parsing addons:", error);
          }

          return (
            <div
              key={index}
              className="cart-item d-flex align-items-center justify-content-between"
            >
              {/* Title and Addons */}
              <div className="cart-item-name flex-grow-1 d-flex flex-column align-items-start">
                <div className="d-flex align-items-center gap-2">
                  <span className="item-name">{item.print_name || item.name}</span>
                  <i
                    className="icofont icofont-pencil"
                    onClick={() => handleEditPrice(item, index)}
                    title="Edit Price"
                    style={{ cursor: "pointer", color: "#1a73e8", fontSize: "15px" }}
                  />
                  <i
                    className="icofont icofont-tag"
                    onClick={() => handleEditPrintName(item, index)}
                    title="Edit Print Name"
                    style={{ cursor: "pointer", color: "#1e8e3e", fontSize: "15px" }}
                  />
                  {item._addonDefinitions && (
                    <i
                      className="icofont icofont-settings-alt"
                      onClick={() => handleEditAddons(item, index)}
                      title="Edit Addons"
                      style={{ cursor: "pointer", color: "#e65100", fontSize: "15px" }}
                    />
                  )}
                </div>
                <span className="item-price">{`${item.price} x ${item.quantity}`}</span>
                <ItemAddonView addons={addons} />
              </div>

              {/* Quantity */}
              <div className="rounded-touchspin">
                <POSTouchSpin
                  color="dark"
                  arrowIcon={true}
                  btnClass={`touchspin-dark`}
                  onDecrement={() => handleDecreaseQuantity(item.id)}
                  onIncrement={() => handleIncreaseQuantity(item.id)}
                  item={item}
                />
              </div>

              {/* Price */}
              <div className="text-center flex-grow-1">
                <span className="cart-item-total">
                  {getFormattedPrice(item.price * item.quantity)}
                </span>
              </div>

              {/* Remove Icon */}
              <div className="remove-item ml-2">
                <i
                  className="icofont icofont-close-line"
                  onClick={() => handleRemoveItem(item.id)}
                  style={{ cursor: "pointer", color: "red" }}
                ></i>
              </div>
            </div>
          );
        })
      )}
    {editingItem && (
        <POSAddonModal
          modal={editModal}
          toggleModal={() => { setEditModal(false); setEditingItem(null); setEditingIndex(null); }}
          product={{ ...editingItem, price: editingItem._basePrice, addons: editingItem._addonDefinitions }}
          addonQuantities={editAddonQuantities}
          setAddonQuantities={setEditAddonQuantities}
          handleAddToCartWithAddons={handleUpdateCartItemAddons}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default CartItems;
